import React from 'react';
import classNames from 'classnames';
import { SectionSplitProps } from '../../utils/SectionProps';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Button from '../elements/Button';
import { submitContactForm } from '../../api/contact';

const propTypes = {
  ...SectionSplitProps.types
};

const defaultProps = {
  ...SectionSplitProps.defaults
};

class SupportSplit extends React.Component {
  state = {
    name: '',
    email: '',
    message: '',
    errors: {},
    submitted: false,
    submitError: '',
    isSubmitting: false
  };

  handleChange = (e) => {
    const { name, value } = e.target;

    this.setState((prevState) => ({
      [name]: value,
      submitted: false,
      submitError: '',
      errors: {
        ...prevState.errors,
        [name]: ''
      }
    }));
  }

  handleSubmit = async (e) => {
    e.preventDefault();

    const { name, email, message } = this.state;
    const errors = {};

    // Validate the contact form client-side before calling the contact API.
    if (!email.trim()) {
      errors.email = 'Please enter your email.';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address.';
    }

    if (!message.trim()) {
      errors.message = 'Please enter a message.';
    }

    if (Object.keys(errors).length > 0) {
      this.setState({ errors, submitted: false, submitError: '' });
      return;
    }

    this.setState({
      isSubmitting: true,
      submitted: false,
      submitError: ''
    });

    try {
      // Send the support request to the contact form API once validation passes.
      await submitContactForm({
        name: name.trim(),
        email: email.trim(),
        message: message.trim()
      });

      this.setState({
        name: '',
        email: '',
        message: '',
        errors: {},
        submitted: true,
        submitError: '',
        isSubmitting: false
      });
    } catch (error) {
      this.setState({
        submitted: false,
        submitError: error.message || 'Unable to send your message right now. Please try again.',
        isSubmitting: false
      });
    }
  }

  render() {
    const {
      className,
      topOuterDivider,
      bottomOuterDivider,
      topDivider,
      bottomDivider,
      hasBgColor,
      invertColor,
      invertMobile,
      invertDesktop,
      alignTop,
      ...props
    } = this.props;

    const { name, email, message, errors, submitted, submitError, isSubmitting } = this.state;

    const outerClasses = classNames(
      'features-split section',
      topOuterDivider && 'has-top-divider',
      bottomOuterDivider && 'has-bottom-divider',
      hasBgColor && 'has-bg-color',
      invertColor && 'invert-color',
      className
    );

    const innerClasses = classNames(
      'features-split-inner section-inner',
      topDivider && 'has-top-divider',
      bottomDivider && 'has-bottom-divider'
    );

    const splitClasses = classNames(
      'split-wrap',
      invertMobile && 'invert-mobile',
      invertDesktop && 'invert-desktop',
      alignTop && 'align-top'
    );

    const sectionHeader = {
      title: 'Need Help?',
      paragraph: 'We’re here to help you get the most out of Exanta.'
    };

    return (
      <section
        {...props}
        className={outerClasses}
      >
        <div className="container">
          <div className={innerClasses}>
            <SectionHeader data={sectionHeader} className="center-content" />
            <div className={splitClasses}>
              <div className="split-item">
                <div className="split-item-content center-content-mobile">
                  <h3 className="mt-0 mb-16 reveal-from-bottom" data-reveal-container=".split-item">
                    Support when you need it
                  </h3>
                  <p className="m-0 mb-24 reveal-from-bottom" data-reveal-delay="100" data-reveal-container=".split-item">
                    If you have questions about setup, usage, or anything else, feel free to reach out.
                  </p>
                  <p className="m-0 mb-12 reveal-from-bottom" data-reveal-delay="150" data-reveal-container=".split-item">
                    <a href="mailto:support@exanta.ai" className="h4">
                      support@exanta.ai
                    </a>
                  </p>
                  <p className="m-0 text-sm text-color-low reveal-from-bottom" data-reveal-delay="200" data-reveal-container=".split-item">
                    We aim to respond as quickly as possible.
                  </p>
                  <p className="m-0 mt-12 text-sm text-color-low reveal-from-bottom" data-reveal-delay="225" data-reveal-container=".split-item">
                    Terms &amp; Conditions available on request.
                  </p>
                </div>

                <div
                  id="support"
                  className="split-item-image center-content-mobile reveal-from-bottom"
                  data-reveal-container=".split-item"
                  data-reveal-delay="250"
                >
                  <div className="tiles-item-inner has-shadow" style={{ width: '100%', textAlign: 'left' }}>
                    <form onSubmit={this.handleSubmit} noValidate>
                      <fieldset>
                        <div className="mb-16">
                          <Input
                            id="support-name"
                            type="text"
                            name="name"
                            label="Name"
                            placeholder="Name"
                            labelHidden
                            value={name}
                            onChange={this.handleChange}
                            hint={errors.name}
                            status={errors.name ? 'error' : ''}
                          />
                        </div>
                        <div className="mb-16">
                          <Input
                            id="support-email"
                            type="email"
                            name="email"
                            label="Email"
                            placeholder="Email"
                            labelHidden
                            value={email}
                            onChange={this.handleChange}
                            required
                            hint={errors.email}
                            status={errors.email ? 'error' : ''}
                          />
                        </div>
                        <div className="mb-24">
                          <Input
                            id="support-message"
                            type="textarea"
                            name="message"
                            label="Message"
                            placeholder="Message"
                            labelHidden
                            value={message}
                            onChange={this.handleChange}
                            rows={5}
                            required
                            hint={errors.message}
                            status={errors.message ? 'error' : ''}
                          />
                        </div>
                        <Button color="primary" wide type="submit" loading={isSubmitting} disabled={isSubmitting}>
                          Send Message
                        </Button>
                        {submitError && (
                          <p className="mb-0 mt-16 text-sm text-color-error">
                            {submitError}
                          </p>
                        )}
                        {submitted && (
                          <p className="mb-0 mt-16 text-sm text-color-low">
                            Thanks. Your message has been sent and we&apos;ll follow up soon.
                          </p>
                        )}
                      </fieldset>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

SupportSplit.propTypes = propTypes;
SupportSplit.defaultProps = defaultProps;

export default SupportSplit;
